<template>
  <availabilities-edit-dialog :availabilities="availabilities" :initializing="initializing" :labels="labels" :removing="loadingAvailabilityUnset"
                              :saving="loadingAvailabilityUpdate" :subtitle="subtitle" :value="value" icon="mdi-account-clock-outline" removable
                              unavailable-text="Wird als nicht verfügbar gespeichert – die Lehrkraft erscheint als nicht verfügbar"
                              @input="reset" @remove="saveUnset" @save="saveUpdate"/>
</template>

<script lang="ts">
import Vue from 'vue';
import {LabeledAvailabilityInterface} from '@/interfaces/availability.interface';
import {EmployeeHandlerAPI} from '@/classes/api/employee-handler.api.class';
import {EmployeeFields} from '@/classes/clientOnly/permissionTreeResources/enums/entityFields/EmployeeFields';
import {Employee} from '@/interfaces/employee.interface';
import mongoose from 'mongoose';
import AvailabilitiesEditDialog from '@/components/common/availability/availabilitiesEditDialog.vue';
import {LABELS_WITH_MAKEUP} from '@/components/common/availability/availabilityEditor.helper';

/** Loads and stores the labeled availabilities of an employee; UI lives in the shared availabilities dialog */
export default Vue.extend({
  components: {AvailabilitiesEditDialog},
  props: {
    value: {
      type: Boolean,
      required: true,
    },
    employeeId: {
      type: mongoose.Types.ObjectId,
      required: true,
    },
  },
  data: () => ({
    /** Standort / Lehrkraft may mark windows "Für Nachholtermine" */
    labels: LABELS_WITH_MAKEUP,
    initializing: false,
    loadingAvailabilityUpdate: false,
    loadingAvailabilityUnset: false,
    /** null = no information stored */
    availabilities: null as LabeledAvailabilityInterface[] | null,
    employee: undefined as unknown as Employee,
  }),
  computed: {
    subtitle(): string {
      const name = this.employee ? `${this.employee.firstName || ''} ${this.employee.lastName || ''}`.trim() : '';
      return name || 'Lehrkraft';
    },
  },
  methods: {
    async initialize() {
      this.initializing = true;
      try {
        this.employee = await EmployeeHandlerAPI.findById(this.employeeId, {
          fields: [EmployeeFields.AVAILABILITIES, EmployeeFields.FIRST_NAME, EmployeeFields.LAST_NAME],
        });
        this.availabilities = this.employee.availabilities || null;
      } catch (e) {
        this.$$showSnackbar('Fehler beim Laden der Verfügbarkeiten', 'error');
        this.$emit('input', false);
      } finally {
        this.initializing = false;
      }
    },
    async saveUpdate(availabilities: LabeledAvailabilityInterface[]) {
      this.loadingAvailabilityUpdate = true;
      try {
        await EmployeeHandlerAPI.update(this.employeeId, {
          availabilities,
        });
        this.$$showSnackbar('Die Verfügbarkeiten wurden erfolgreich gespeichert', 'success');
        this.$emit('update:availabilities', availabilities);
        this.reset();
      } catch (e) {
        this.$$showSnackbar('Fehler beim Speichern der Verfügbarkeiten', 'error');
      } finally {
        this.loadingAvailabilityUpdate = false;
      }
    },
    /** Former "Verfügbarkeiten verwerfen": unsets the availabilities (no information instead of "not available") */
    async saveUnset() {
      this.loadingAvailabilityUnset = true;
      try {
        await EmployeeHandlerAPI.update(this.employeeId, {availabilities: null});
        this.$$showSnackbar('Verfügbarkeiten wurden verworfen', 'success');
        this.$emit('update:availabilities', undefined);
        this.reset();
      } catch (e) {
        this.$$showSnackbar('Fehler beim Löschen der Verfügbarkeiten', 'error');
      } finally {
        this.loadingAvailabilityUnset = false;
      }
    },
    reset() {
      this.$emit('input', false);
      setTimeout(() => {
        this.employee = undefined as unknown as Employee;
        this.availabilities = null;
      }, 300);
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(v: boolean) {
        if (v) {
          this.initialize();
        }
      },
    },
  },
});
</script>
